import { useData } from 'vike-react/useData';
import type { Data } from './+data';
import { BlockRenderer, type BlockData } from '../../components/blocks/BlockRenderer';
import { HeroMinimal } from '../../components/blocks/HeroMinimal';
import { AudienceHub } from '../../components/blocks/AudienceHub';
import { PolesTriptyque } from '../../components/blocks/PolesTriptyque';
import { PartnersMarquee } from '../../components/home/PartnersMarquee';
import { ToolsBento } from '../../components/home/ToolsBento';
import { SectorsCarousel } from '../../components/home/SectorsCarousel';
import { HowItWorks } from '../../components/home/HowItWorks';
import { CaseClientsCarousel } from '../../components/home/CaseClientsCarousel';
import { ProofKPI } from '../../components/home/ProofKPI';
import { FinalImmersiveCTA } from '../../components/home/FinalImmersiveCTA';
import { LocalBusinessLd } from '../../components/seo/jsonld/LocalBusiness';
import { useAudienceContent } from '../../hooks/useAudienceContent';
import { HOME_HERO_CONTENT } from '../../config/home-hero-content';

const HERO_TYPES = new Set(['HeroMinimal', 'HeroPremium', 'HeroPrimary', 'HeroPillar']);

function toBlockData(blocks: NonNullable<Data['page']>['blocks']): BlockData[] {
  return blocks.map((b) => ({
    id: b.id,
    type: b.type,
    payload: b.payload,
    audienceFilter: (b.audienceFilter as BlockData['audienceFilter']) ?? null,
  }));
}

export default function HomePage() {
  const { page } = useData<Data>();
  const heroContent = useAudienceContent(HOME_HERO_CONTENT);

  if (!page) {
    return (
      <>
        <LocalBusinessLd />
        <HeroMinimal
          eyebrow={heroContent.eyebrow}
          title={heroContent.title}
          titleAccent={heroContent.titleAccent}
          sub={heroContent.sub}
          chip={heroContent.chip}
          primaryCta={heroContent.primaryCta}
          secondaryCta={heroContent.secondaryCta}
        />
        <PartnersMarquee />
        <AudienceHub />
        <ToolsBento />
        <SectorsCarousel />
        <PolesTriptyque />
        <HowItWorks />
        <CaseClientsCarousel />
        <ProofKPI />
        <FinalImmersiveCTA />
      </>
    );
  }

  const allBlocks = toBlockData(page.blocks);
  const heroBlocks = allBlocks.filter((b) => HERO_TYPES.has(b.type));
  const restBlocks = allBlocks.filter((b) => !HERO_TYPES.has(b.type));
  const blockTypes = new Set(allBlocks.map((block) => block.type));
  const hasAnyBlock = (...types: string[]) => types.some((type) => blockTypes.has(type));

  return (
    <>
      <LocalBusinessLd />
      <BlockRenderer blocks={heroBlocks} />
      <PartnersMarquee />
      {/* Sections signature toujours présentes — le CMS ne doit pas les faire disparaître */}
      {!hasAnyBlock('AudienceHub') && <AudienceHub />}
      <BlockRenderer blocks={restBlocks} />
      <ToolsBento />
      <SectorsCarousel />
      {!hasAnyBlock('PolesTriptyque') && <PolesTriptyque />}
      <HowItWorks />
      <CaseClientsCarousel />
      <ProofKPI />
      <FinalImmersiveCTA />
    </>
  );
}
